// frontend/src/components/settings/ExactItemResolveDialog.jsx
//
// The item gate's instance of the generic action-model Resolve wizard
// (ResolveGateDialog). Items are high-cardinality, so the backend's multi-signal
// cascade (barcode → article code) does the matching and labels each row with
// the signal that paired it; the help/summary text is match-strategy-aware. The
// shared component owns the interaction (link/import/skip + "Match to…" picker);
// this file is just the item-specific config.

import {
  useExactItemSelection,
  useExactConfirmItems,
} from '../../hooks/useConnectorMutations';
import { useItemSearchCopy, useItemSuggestions } from '../../features/item-filter';
import { formatItemLabel } from '../../utils/itemLabel';
import ResolveGateDialog from './ResolveGateDialog';

// The article code is one cross-origin identifier (shopify_sku / erp_code /
// ItemCode).
const SIGNAL_LABEL_KEYS = {
  barcode: 'connectors.matchKeyBarcode',
  article_code: 'connectors.matchKeyArticleCode',
  manual: 'connectors.matchKeyManual',
};

const ITEM_CONFIG = {
  rowsKey: 'items',
  matchIdField: 'match_item_id',
  matchNameField: 'match_item_name',
  actionIdField: 'item_id',
  signalLabelKeys: SIGNAL_LABEL_KEYS,
  columns: [
    { header: 'connectors.itemColCode', render: (r) => r.code || '—' },
    { header: 'connectors.itemColDescription', render: (r) => r.description || r.erp_id },
    {
      header: 'connectors.itemColCost',
      render: (r, _t, fmt) => (r.cost != null ? fmt.formatCurrency(r.cost, r.cost_currency) : '—'),
    },
  ],
  searchOption: {
    id: (o) => o.item_id,
    label: formatItemLabel,
  },
  pickerSubtitle: (r) => `${r.code || r.erp_id}${r.description ? ` — ${r.description}` : ''}`,
  useSelection: useExactItemSelection,
  useConfirm: useExactConfirmItems,
  useSearch: useItemSuggestions,
  useSearchCopy: useItemSearchCopy,
  text: {
    title: 'connectors.itemResolveTitle',
    empty: 'connectors.itemResolveEmpty',
    emptyConfirmed: 'connectors.itemResolveEmptyConfirmed',
    colMatch: 'connectors.itemColMatch',
    matchedTo: 'connectors.itemMatchedTo',
    willImport: 'connectors.itemWillImport',
    reasons: {
      ambiguous: 'connectors.itemReasonAmbiguous',
      no_match: 'connectors.itemReasonNoMatch',
      no_key: 'connectors.itemReasonNoKey',
      manual: 'connectors.itemReasonManual',
    },
    selectAllMatched: 'connectors.itemSelectAllMatched',
    importAllNew: 'connectors.itemImportAllNew',
    confirm: 'connectors.itemConfirm',
    confirmWithImport: 'connectors.itemConfirmWithImport',
    confirmFailed: 'connectors.itemConfirmFailed',
    pickerTitle: 'connectors.itemPickerTitle',
    pickerNoResults: 'connectors.itemPickerNoResults',
    // Help + summary are match-strategy-aware (auto / single key / manual).
    help: (t, { match_strategy: strategy }) => {
      if (strategy === 'auto') return t('connectors.itemResolveHelpAuto');
      if (strategy === 'manual') return t('connectors.itemResolveHelpManual');
      return t('connectors.itemResolveHelp', { key: t(SIGNAL_LABEL_KEYS[strategy]) });
    },
    summary: (t, { match_strategy: strategy, counts }) => {
      const common = {
        auto: counts.auto_matched,
        total: counts.total,
        attention: counts.ambiguous + counts.unmatched,
      };
      if (strategy === 'auto') return t('connectors.itemMatchSummaryAuto', common);
      return t('connectors.itemMatchSummary', { ...common, key: t(SIGNAL_LABEL_KEYS[strategy]) });
    },
  },
};

export default function ExactItemResolveDialog(props) {
  return <ResolveGateDialog config={ITEM_CONFIG} {...props} />;
}
